import React, { useCallback } from "react";
import { Dialog, DialogContent, DialogActions, Button, Stack, FormControlLabel } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ClosableDialogTitle from "common/ClosableDialogTitle";
import { Field as FormikField, Form as FormikForm, Formik } from "formik";
import { TextField as FormikTextField, Checkbox as FmuiCheckbox } from "formik-mui";
import * as Yup from "yup";
import { PdCourse } from "./models";
import { gql, Reference, useMutation } from "@apollo/client";
import { useNotifications } from "notifications";

// Define your mutations
const CREATE_PD_COURSE = gql`
  mutation CreatePdCourse($pdCourse: PdCourseInsert) {
    pdCourses {
      create(pdCourse: $pdCourse) {
        id
        name
        shortName
        productCode
        isActive
        topicName
        topicShortName
      }
    }
  }
`;

// Define a fragment for the PdCourse type to use in cache operations
const PD_COURSE_FRAGMENT = gql`
  fragment PdCourseFields on PdCourse {
    id
    name
    shortName
    productCode
    isActive
    topicName
    topicShortName
  }
`;

interface PdCoursesDialogProps {
  open: boolean;
  onClose: () => void;
}

// TODO: When using optimistic responses, ensure the UI filters and sorts correctly with the new data.

const PdCoursesDialog: React.FunctionComponent<PdCoursesDialogProps> = ({ open, onClose }) => {
  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    productCode: Yup.string(),
    shortName: Yup.string(),
    topicName: Yup.string(),
    topicShortName: Yup.string(),
  });

  const initialValues: Omit<PdCourse, "id"> = {
    name: "",
    productCode: "",
    shortName: "",
    topicName: "",
    topicShortName: "",
    isActive: false,
  };

  const notifications = useNotifications();

  // Setup mutation for create with improved cache handling
  const [createMutation, { loading: createLoading }] = useMutation(CREATE_PD_COURSE, {
    update(cache, { data }) {
      try {
        const newCourse = data?.pdCourses?.create;
        if (!newCourse) return;

        // Use cache.modify to update all cached queries that include pdCourses
        cache.modify({
          fields: {
            pdCourses(existingCourses: readonly Reference[] = [], { readField }): Reference[] {
              const exists = existingCourses.some((ref) => readField("id", ref) === newCourse.id);
              if (exists) return existingCourses as Reference[];

              const newCourseRef = cache.writeFragment({
                data: newCourse,
                fragment: PD_COURSE_FRAGMENT,
              });

              return [...existingCourses, ...(newCourseRef ? [newCourseRef] : [])];
            },
          },
        });
      } catch (error) {
        console.error("Error updating cache:", error);
        notifications.error("Failed to update cache after creating PD Course");
      }
    },
  });

  // Memoize the submit handler to prevent recreations
  const handleSubmit = useCallback(
    async (values: Omit<PdCourse, "id">) => {
      try {
        await createMutation({
          variables: {
            pdCourse: values,
          },
          optimisticResponse: {
            pdCourses: {
              create: {
                ...values,
                id: -1, // Temporary ID for optimistic create
                __typename: "PdCourse",
              },
              __typename: "PdCourseMutations",
            },
          },
        });
        onClose();
      } catch (error) {
        console.error("Error saving PD course:", error);
        notifications.error("Failed to save PD Course: " + error);
      }
    },
    [createMutation, onClose, notifications],
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth={true} scroll="paper" maxWidth="sm">
      <ClosableDialogTitle onClose={onClose}>{"Add PD Course"}</ClosableDialogTitle>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema} enableReinitialize={true}>
        {(formikProps) => (
          <FormikForm>
            <DialogContent>
              <Stack spacing={2}>
                <FormikField component={FormikTextField} name="name" label="Name" fullWidth required />
                <FormikField component={FormikTextField} name="productCode" label="Product Code" fullWidth />
                <FormikField component={FormikTextField} name="shortName" label="Short Name" fullWidth />
                <FormikField component={FormikTextField} name="topicName" label="Topic Name" fullWidth />
                <FormikField component={FormikTextField} name="topicShortName" label="Topic Short Name" fullWidth />
                <FormControlLabel control={<FormikField name="isActive" component={FmuiCheckbox} type="checkbox" />} label="Active" />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>Cancel</Button>
              <LoadingButton
                color="primary"
                variant="contained"
                loading={formikProps.isSubmitting || createLoading}
                onClick={() => formikProps.submitForm()}
              >
                Submit
              </LoadingButton>
            </DialogActions>
          </FormikForm>
        )}
      </Formik>
    </Dialog>
  );
};

export default PdCoursesDialog;
